import { useEffect } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { featuredMovies, latestUpdates } from '@/mocks/siteData';

const articleDetails: Record<string, {
  content: string;
  director: string;
  cast: string;
  duration: string;
  releaseDate: string;
}> = {
  '1': {
    director: '郭帆',
    cast: '吴京、刘德华、李雪健',
    duration: '173分钟',
    releaseDate: '2026-01-29',
    content: '《流浪地球3》是中国科幻电影的巅峰之作。影片延续前作的故事线，讲述了太阳急速老化膨胀，人类面临灭顶之灾的危急时刻，全球联合政府决定启动"流浪地球计划"。\n\n影片中，地球表面建造了上万座行星发动机，推动地球离开太阳系，前往4.2光年外的新家园。在这个过程中，人类不仅面临宇宙空间的极端环境挑战，还要应对内部的分歧与矛盾。\n\n导演郭帆以宏大的叙事视角和细腻的情感刻画，展现了人类在绝境中的坚韧与团结。影片特效制作精良，行星发动机的壮观场景、木星引力危机的紧张氛围，都达到了世界级水准。\n\n《流浪地球3》不仅是一部科幻灾难片，更是一部关于希望、勇气和人类命运共同体的史诗。影片上映后，全球票房突破50亿，成为中国电影史上又一里程碑。',
  },
  '2': {
    director: '曹盾',
    cast: '雷佳音、易烊千玺',
    duration: '48集',
    releaseDate: '2025-06-15',
    content: '《长安十二时辰》改编自马伯庸同名小说，讲述了唐朝天宝三年上元节前夕，长安城陷入一场惊天危机。\n\n死囚张小敬（雷佳音饰）被靖安司司丞李必（易烊千玺饰）从死牢中提出，要求在十二时辰内找出潜入长安的恐怖分子，阻止一场可能毁灭整个长安城的阴谋。\n\n全剧以时辰为单位推进剧情，每集对应一个时辰，节奏紧凑、悬念迭起。剧中对唐朝长安城的还原度极高，从建筑、服饰到饮食、礼仪，都经过严格考究。\n\n该剧不仅是一部扣人心弦的悬疑剧，更是一幅盛唐风貌的壮丽画卷。2025年播出后，豆瓣评分高达8.8分，被誉为年度最佳古装剧。',
  },
  '3': {
    director: '严敏',
    cast: '黄渤、孙红雷、黄磊、王迅、张艺兴',
    duration: '12期',
    releaseDate: '2026-04-18',
    content: '《极限挑战第十季》是中国最具影响力的户外真人秀节目之一。本季节目延续"这就是命"的口号，六位固定成员继续在全国各地展开各种极限挑战。\n\n本季节目在任务设计上更加大胆创新，从城市街头到自然荒野，从智力博弈到体力对抗，每一期都充满惊喜和笑料。成员之间的化学反应依旧是节目最大的看点，兄弟情深与互坑互损并存。\n\n第十季特别邀请了多位重量级飞行嘉宾，包括演艺圈、体育界和文化界的大咖，碰撞出更多精彩火花。节目播出期间，收视率稳居同时段第一，网络话题阅读量突破200亿。',
  },
};

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const allMovies = [...featuredMovies, ...latestUpdates];
  const movie = allMovies.find((m) => m.id === id);
  const detail = id ? articleDetails[id] : null;

  if (!movie) {
    return (
      <>
        <Navbar />
        <main className="pt-20 md:pt-24">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-20 text-center">
            <p className="text-foreground-500">内容未找到</p>
            <Link to="/" className="mt-4 inline-block px-6 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const categoryLabel: Record<string, string> = {
    movie: '电影',
    tv: '电视剧',
    variety: '综艺',
    anime: '动漫',
  };

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: movie.title,
    description: movie.description,
    datePublished: 'episode' in movie ? movie.episode : movie.updateTime,
    author: { '@type': 'Organization', name: '天美麻花星空影视' },
  };

  const breadcrumbList = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.tianmeimahua.com/' },
      { '@type': 'ListItem', position: 2, name: categoryLabel[movie.category] || movie.category, item: `https://www.tianmeimahua.com/category/${movie.category}` },
      { '@type': 'ListItem', position: 3, name: movie.title, item: `https://www.tianmeimahua.com/article/${movie.id}` },
    ],
  };

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbList) }} />
      <Navbar />
      <main className="pt-20 md:pt-24">
        <div className="max-w-4xl mx-auto px-4 md:px-6 py-8">
          <nav className="flex items-center gap-2 text-xs md:text-sm text-foreground-500 mb-6 flex-wrap" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">首页</Link>
            <span>/</span>
            <Link to={`/category/${movie.category}`} className="hover:text-primary-500 transition-colors cursor-pointer">
              {categoryLabel[movie.category] || movie.category}
            </Link>
            <span>/</span>
            <span className="text-foreground-900 font-medium">{movie.title}</span>
          </nav>

          <article>
            <div className="flex flex-col md:flex-row gap-6 md:gap-8 mb-8">
              <div className="w-full md:w-64 flex-shrink-0">
                <div className="rounded-lg overflow-hidden aspect-[3/4] bg-background-200">
                  <img
                    src={movie.imageUrl}
                    alt={movie.title}
                    className="w-full h-full object-cover object-top"
                    title={`${movie.title} - 天美麻花星空影视`}
                  />
                </div>
              </div>

              <div className="flex-1">
                <div className="flex items-center gap-2 mb-3">
                  <span className="inline-block px-2.5 py-1 rounded text-xs font-medium bg-primary-500 text-background-50">
                    {categoryLabel[movie.category] || movie.category}
                  </span>
                  <span className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded text-sm font-bold text-accent-500">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-star-fill text-xs" />
                    </div>
                    {movie.rating}
                  </span>
                </div>

                <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-3">
                  {movie.title}
                </h1>

                <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-5">
                  {movie.description}
                </p>

                {detail && (
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-3">
                      <span className="text-foreground-500 w-16">导演：</span>
                      <span className="text-foreground-800 font-medium">{detail.director}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-foreground-500 w-16">主演：</span>
                      <span className="text-foreground-800">{detail.cast}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-foreground-500 w-16">片长：</span>
                      <span className="text-foreground-800">{detail.duration}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-foreground-500 w-16">上映：</span>
                      <span className="text-foreground-800">{detail.releaseDate}</span>
                    </div>
                  </div>
                )}

                {movie.tags && (
                  <div className="flex flex-wrap gap-2 mt-5">
                    {movie.tags.map((tag) => (
                      <span key={tag} className="inline-block px-3 py-1 rounded-full text-xs bg-secondary-100 text-secondary-700">
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {detail && (
              <div className="prose prose-sm md:prose-base max-w-none">
                {detail.content.split('\n\n').map((paragraph, idx) => (
                  <p key={idx} className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                    {paragraph}
                  </p>
                ))}
              </div>
            )}
          </article>
        </div>
      </main>
      <Footer />
    </>
  );
}